import { useEffect } from 'react';

import type { IntersectingItem, RegisteredSection } from './types.ts';

const createMutableList = <T extends { id: string }>() => {
  const list = new Array<T>();
  const getIndex = (id: string) => {
    const index = list.findIndex((item) => item.id === id);
    return index === -1 ? null : index;
  };
  return {
    update: (item: T) => {
      const index = getIndex(item.id) ?? list.length;
      list[index] = item;
    },
    delete: (itemId: string) => {
      const index = getIndex(itemId);
      if (index) list.splice(index, 1);
    },
    unwrap: () => list,
    clear: () => (list.length = 0),
  };
};

export function useIntersectedItems(
  onIntersectionCb: (intersectedItems: IntersectingItem[]) => void,
  intersectionObserverSettings: IntersectionObserverInit,
  sections: Map<string, RegisteredSection>,
) {
  useEffect(() => {
    const intersectedItems = createMutableList<IntersectingItem>();
    const sectionsArray = Array.from(sections.values());
    const observer = new IntersectionObserver((entries) => {
      const elementToId = new Map(sectionsArray.map(({ id, ref }) => [ref.current, id]));

      entries.forEach((entry) => {
        const elementId = elementToId.get(entry.target);
        if (!elementId) return; // Skip if not found in our registered sections

        if (entry.isIntersecting) {
          const intersectingItem: IntersectingItem = {
            id: elementId,
            intersectionRatio: entry.intersectionRatio,
            target: entry.target,
          };
          intersectedItems.update(intersectingItem);
        } else {
          intersectedItems.delete(elementId);
        }
      });
      onIntersectionCb(intersectedItems.unwrap());
    }, intersectionObserverSettings);

    sectionsArray.forEach(({ ref: { current: element } }) => {
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect();
      intersectedItems.clear();
    };
  }, [intersectionObserverSettings, sections, onIntersectionCb]);
}
